import React, { Suspense, useContext, useEffect, useState } from 'react';
import { GridLoader } from 'react-spinners';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router, Link, Route, Routes, useLocation } from 'react-router-dom';
import { chakra, ChakraProvider, Text } from '@chakra-ui/react';
import { theme } from './ChakraTheme';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import styled, { createGlobalStyle } from '../consts/styled';
import { zIndex, colors, spacing, media } from './ChakraTheme';
import { CloudinaryContext } from 'cloudinary-react';
import { NavContainer } from '../components/Navigation/NavContainer';
import { GuidesPage, UnitPage, ArticlePage } from '../modules/app';
import { SignInPage, SignOutPage } from '../modules/auth';

import Page404 from '../general-pages/Page404';
import AuthProvider, { AuthContext } from '../context/AuthProvider';
import Navigation from '../modules/navigation';
import ForbiddenPage from '../general-pages/ForbiddenPage';
import ScrollToTop from '../modules/navigation/ScrollToTop';
import { ActivityTrackerGate } from '../components/ActivityTrackerGate';
import { GroupWorkCheckInGate } from '../components/GroupWorkCheckInGate';
import { NewPlatformPromptGate } from '../components/NewPlatformPromptGate';

import ChatContainer from './ChatContainer';
import { DashboardPage, ClassPage } from '../modules/app';

import { trackAction } from '../helpers/trackAction';
import { axiosWrapper } from '../hooks/axios';
import ModalContainer from '../components/ModalContainer';
import Help from '../components/Help';
import Changelog from '../components/Changelog';
import InfoMainPage from '../modules/info/InfoMainPage';
import ProgramInfoPage from '../modules/info/ProgramInfoPage';
import InteractiveTestPage from '../modules/app/pages/InteractiveTest/InteractiveTestPage';
import SignUpPage from '../modules/auth/pages/SignUpPage';

const LazyActivateConfirmedPage = React.lazy(() => import('../modules/auth/pages/ActivateConfirmedPage'));
const LazyActivateSetPasswordPage = React.lazy(() => import('../modules/auth/pages/ActivateSetPasswordPage'));
const LazyInviteCodeHomePage = React.lazy(() => import('../modules/auth/pages/InviteCodePage'));
const LazyInviteCodePage = React.lazy(() => import('../modules/auth/pages/InviteCodePage/show'));
const LazyPasswordResetCompletePage = React.lazy(() => import('../modules/auth/pages/PasswordResetCompletePage'));
const LazyPasswordResetDonePage = React.lazy(() => import('../modules/auth/pages/PasswordResetDonePage'));
const LazyPasswordResetPage = React.lazy(() => import('../modules/auth/pages/PasswordResetPage'));
const LazyPasswordResetSetPasswordPage = React.lazy(() => import('../modules/auth/pages/PasswordResetSetPasswordPage'));

const LazyDemoSignUpPage = React.lazy(() => import('../modules/app/pages/DemoSignUp'));
const LazySignUpEvolveMeStudyGroupPage = React.lazy(() => import('../modules/app/pages/SignUpEvolveMeStudyGroup'));
const LazyPrivacyPage = React.lazy(() => import('../modules/legal/PrivacyPage'));
const LazyTermsPage = React.lazy(() => import('../modules/legal/TermsPage'));
const LazyAccessibilityStatementPage = React.lazy(() => import('../modules/legal/AccessibilityStatementPage'));

const LazyEditStudyGroupPage = React.lazy(() => import('../modules/app/pages/Licenses/edit/'));
const LazyDeleteStudentsPage = React.lazy(() => import('../modules/app/pages/Licenses/DeleteStudentsPage/'));
const LazyManageDevicesPage = React.lazy(() => import('../modules/app/pages/Licenses/ManageDevicesPage'));
const LazyStaffPanelPage = React.lazy(() => import('../modules/app/pages/StaffPanel/'));
const LazySkolaBuducnostiPage = React.lazy(() => import('../modules/app/pages/SkolaBuducnosti'));
const LazyLicensesPage = React.lazy(() => import('../modules/app/pages/Licenses/index'));
const LazySettingsPage = React.lazy(() => import('../modules/auth/pages/SettingsPage'));
const LazyProfilePage = React.lazy(() => import('../modules/app/pages/Profile'));
const LazyTrackEditPage = React.lazy(() => import('../modules/app/pages/StaffPanel/subpages/TrackEditPage'));
const LazyDemoAskInfoPage = React.lazy(() => import('../modules/auth/pages/DemoAskInfoPage'));

const LazyEventLogPage = React.lazy(() => import('../modules/app/pages/StaffPanel/EventLogPage'));
const LazyMyPrograms = React.lazy(() => import('../modules/app/pages/MyPrograms'));
const LazyAssistants = React.lazy(() => import('../modules/app/pages/Assistants'));
const LazyLicenseExpired = React.lazy(() => import('../modules/app/pages/Licenses/LicenseExpired'));
const LazySignUpPage = React.lazy(() => import('../modules/auth/pages/SignUpPage'));
const LazyEditorPage = React.lazy(() => import('../modules/app/pages/EditorPage'));

const ModalGlobalStyle = createGlobalStyle`
  html, body {
    height: 100%;
  }

  #universe {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }

  div[data-tippy-root] {
    z-index: ${zIndex.MODAL + 1} !important;
  }

  .max-lines {
    -webkit-box-orient: vertical !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 3 !important;
    overflow: hidden !important;
    padding-bottom: 0 !important;
  }

  /* ===== Dark / High-Contrast mode — styled-components overrides ===== */
  /* Chakra handles its own components automatically via colorMode.       */
  /* These rules cover elements that live outside Chakra's style system.  */

  body.chakra-ui-dark {
    background-color: #0f172a;
  }

  /* Main navigation header (styled.header with hardcoded light bg) */
  body.chakra-ui-dark header {
    background-color: #1e293b !important;
    border-bottom: 1px solid #334155 !important;
  }

  /* Nav link h5 text (styled-components, hardcoded darkblue) */
  body.chakra-ui-dark nav h5,
  body.chakra-ui-dark .navigation-link h5 {
    color: #f1f5f9 !important;
  }

  /* Mobile sidebar modal */
  body.chakra-ui-dark .chakra-modal__content {
    background-color: #1e293b !important;
  }

  /* Sidebar link text */
  body.chakra-ui-dark .navigation-link {
    color: #f1f5f9 !important;
  }

  /* Nav separator lines */
  body.chakra-ui-dark .navigation-links-separator {
    border-color: #334155 !important;
  }

  /* ===== Nav link active/hover bottom border ===== */
  /* navLinkStyle &.active::before { background-color: colors.darkblue } — invisible on dark header */
  body.chakra-ui-dark header a.active::before {
    background-color: #f1f5f9 !important;
  }
  body.chakra-ui-dark header a:hover::before {
    background-color: rgba(241, 245, 249, 0.6) !important;
  }

  /* ===== "Only active" checkbox label ===== */
  /* SchoolFilter wraps in <Stack color="black"> forcing black text on Checkbox label */
  body.chakra-ui-dark .chakra-checkbox__label {
    color: #f1f5f9 !important;
  }

  /* ===== Program/classroom description text ===== */
  /* ClassComponent.tsx: <Text as="div" color="#595959"> — dark gray invisible on dark bg */
  body.chakra-ui-dark [data-cy="dashboard-class"] div.chakra-text {
    color: #e2e8f0 !important;
  }

  /* ===== Footer ===== */
  body.chakra-ui-dark .footer {
    background-color: #1e293b !important;
    border-top: 1px solid #334155 !important;
  }
  body.chakra-ui-dark .footer-copy {
    color: #f1f5f9 !important;
  }
  body.chakra-ui-dark .pdf-link {
    color: #f1f5f9 !important;
  }
  body.chakra-ui-dark .pdf-link:hover {
    color: #fff !important;
  }
  body.chakra-ui-dark .footer-sep {
    color: #64748b !important;
  }

  /* ===== Class page tabs ===== */
  body.chakra-ui-dark [role="tablist"] li,
  body.chakra-ui-dark [role="tablist"] li .caption {
    color: #f1f5f9 !important;
  }
  body.chakra-ui-dark [role="tablist"] li.active {
    border-bottom-color: #f1f5f9 !important;
  }
  body.chakra-ui-dark [role="tablist"] li.disabled p {
    color: #64748b !important;
  }

  /* ===== Lesson sidebar ===== */
  /* AccordionIcon color="blue" → white in dark mode */
  body.chakra-ui-dark .sidebar .chakra-accordion__icon {
    color: #f1f5f9 !important;
  }
  /* Module number circles */
  body.chakra-ui-dark .module-title::before {
    background-color: #1e293b !important;
    border-color: #475569 !important;
    color: #94a3b8 !important;
  }
  body.chakra-ui-dark .active-module.module-title::before {
    border-color: #f1f5f9 !important;
    color: #f1f5f9 !important;
  }
  /* Keep limegreen for completed modules */
  body.chakra-ui-dark .done.module-title::before {
    background-color: #75d640 !important;
    border-color: #75d640 !important;
    color: #fff !important;
  }
  /* Connector line after circles */
  body.chakra-ui-dark .module-title::after {
    background-color: #334155 !important;
  }

  /* ===== Sidebar content list (LessonName items) ===== */
  /* Active item: colors.darkblue (#102e8e); inactive: colors.midnight@80% — both invisible on dark */
  body.chakra-ui-dark .sidebar .body-x-small {
    color: #f1f5f9 !important;
  }
  /* Content item dot — was white bg with darkblue/mischka border */
  body.chakra-ui-dark .sidebar .circle {
    background-color: #1e293b !important;
    border-color: #475569 !important;
  }

  /* ===== Back button in lesson header ===== */
  /* Title.tsx: color="rgb(16, 46, 142)" hardcoded + colorScheme blue (theme blue.500 = #102e8e) */
  body.chakra-ui-dark .back,
  body.chakra-ui-dark .back svg {
    color: #f1f5f9 !important;
  }

  /* ===== Lesson / unit title text ===== */
  /* Unit name h1 (Title.tsx header) and content name h2 (Toolbar) */
  body.chakra-ui-dark header h1,
  body.chakra-ui-dark header h2,
  body.chakra-ui-dark [data-cy="content-title"] {
    color: #f1f5f9 !important;
  }

  /* ===== Lesson toolbar ===== */
  body.chakra-ui-dark .toolbar-scroll {
    background-color: #0f172a !important;
  }
  body.chakra-ui-dark .toolbar-scroll::before {
    background: #0f172a !important;
  }

  /* ===== Lesson progress bar ===== */
  body.chakra-ui-dark .content-nav-row .chakra-progress {
    background: #334155 !important;
    background-color: #334155 !important;
  }
  body.chakra-ui-dark .content-nav-row .chakra-progress__indicator {
    background: #75d640 !important;
    background-color: #75d640 !important;
  }

  /* ===== Prev/next lesson navigation buttons ===== */
  body.chakra-ui-dark .content-nav-row .chakra-button {
    background-color: #3b82f6 !important;
    color: #fff !important;
    border-color: #3b82f6 !important;
  }
  body.chakra-ui-dark .content-nav-row .chakra-button:hover:not([disabled]) {
    background-color: #2563eb !important;
  }
  body.chakra-ui-dark .content-nav-row .chakra-button[disabled] {
    background-color: #334155 !important;
    border-color: #334155 !important;
    color: #94a3b8 !important;
    opacity: 1 !important;
  }

  /* ===== prefers-contrast: more ===== */
  @media (prefers-contrast: more) {
    /*
     * Override Chakra's generated CSS variables for low-contrast gray tones.
     * gray.400 (#a0aec0) is 2.7:1 on white — fails AA.
     * gray.500 (#718096) is 4.6:1 — borderline.
     * slategray (#8e8fa2) and steel (#8092b0) are ~2.8:1 — fail AA.
     * gray.200 borders (#e2e8f0) are nearly invisible.
     */
    :root {
      --chakra-colors-gray-200: #6b7280 !important;
      --chakra-colors-gray-300: #5a6478 !important;
      --chakra-colors-gray-400: #2d3748 !important;
      --chakra-colors-gray-500: #1a202c !important;
      --chakra-colors-slategray-500: #2d3748 !important;
      --chakra-colors-slategray-600: #2d3748 !important;
      --chakra-colors-steel-500: #1e3a5f !important;
      --chakra-colors-steel-600: #1e3a5f !important;
    }

    /* Placeholder text: make it opaque and dark enough to read */
    ::placeholder {
      color: #2d3748 !important;
      opacity: 1 !important;
    }

    /* Form controls: enforce a clearly visible border */
    input, textarea, select {
      border-color: #1a202c !important;
      border-width: 2px !important;
    }

    /* Focus ring: maximum contrast on any background */
    :focus-visible {
      outline: 3px solid #000 !important;
      outline-offset: 3px !important;
      box-shadow: none !important;
    }
  }
`;

const NavWrapper: React.FC = () => {
  const { pathname } = useLocation();
  if (pathname === '/editor/' || pathname === '/script-writer/') return null;
  return <Navigation hideSecondRow={false} />;
};

const SkipLink = styled.a`
  position: absolute;
  top: -100%;
  left: 0;
  padding: 8px 16px;
  background: #102e8e;
  color: white;
  font-weight: bold;
  z-index: ${zIndex.MODAL + 2};
  &:focus {
    top: 0;
  }
`;

const LoaderContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const App = styled(({ className }) => {
  const user = useContext(AuthContext);
  const { t } = useTranslation();
  const [interval, setSetInterval] = useState<any>();

  useEffect(() => {
    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    const viewport = { width: vw, height: vh };

    trackAction(
      {
        action_type: 'app_load',
        data: {
          viewport,
          userAgent: navigator.userAgent,
        },
      },
      user?.user_actions,
    );



    if (interval !== undefined && interval !== null) {
      clearInterval(interval);
      setSetInterval(null);
    }
    if (document.hasFocus()) {
      const url = window.location.pathname;
      const top = document.scrollingElement?.scrollTop || 0;
      const left = document.scrollingElement?.scrollLeft || 0;
      axiosWrapper('/api/v1/records/heartbeat/', 'post', { url, scroll: { top, left } });
    }
    setSetInterval(
      setInterval(() => {
        if (document.hasFocus()) {
          const url = window.location.pathname;
          const top = document.scrollingElement?.scrollTop || 0;
          const left = document.scrollingElement?.scrollLeft || 0;
          axiosWrapper('/api/v1/records/heartbeat/', 'post', { url, scroll: { top, left } });
        }
      }, 2 * 60000), // 2 minutes
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className={className}>
      <ChakraProvider theme={theme}>
        <AuthProvider>
          <DndProvider backend={HTML5Backend}>
            <CloudinaryContext cloudName="dtg18yyuk">
              <ModalGlobalStyle />
              <Helmet>
                <meta name="viewport" content="initial-scale=1,width=device-width" />
              </Helmet>
              <Router>
                <ActivityTrackerGate />
                <GroupWorkCheckInGate />
                <NewPlatformPromptGate />
                <main id="main-content" className="main">
                  <SkipLink href="#page-content">Skip to main content</SkipLink>
                  <NavWrapper />
                  <ScrollToTop ignored={[UnitPage.unit.getRoute(), UnitPage.content.getRoute(), UnitPage.module.getRoute(), ClassPage.getRoute()]} />
                  <div id="page-content" tabIndex={-1}>
                  <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                    <Routes>
                      {/* <Route
                      path="/foobar"
                      element={() => {
                        // @ts-ignore
                        return <button onClick={() => noMethod}>This breaks stuff</button>;
                      }}
                    /> */}
                      <Route path={DashboardPage.getRoute()} element={<DashboardPage />} />
                      <Route path={'/demo-info/'} element={<LazyDemoAskInfoPage />} />
                      <Route path={'/demo-sign-up/:demoCode/'} element={<LazyDemoSignUpPage />} />
                      <Route path={'/sign-up/evolveme/'} element={<LazySignUpEvolveMeStudyGroupPage />} />
                      <Route path={'/licenses/:licenseId'} element={<LazyEditStudyGroupPage />} />
                      <Route path={'/unenrolled-students/:schoolId/'} element={<LazyDeleteStudentsPage />} />
                      <Route path={'/manage-devices/:schoolId?/'} element={<LazyManageDevicesPage />} />
                      <Route path={'/staffpanel/:activeTab?/'} element={<LazyStaffPanelPage />} />
                      <Route path={'/staffpanel/tracks/edit/:trackId/'} element={<LazyTrackEditPage />} />
                      <Route path={'/skola-buducnosti/'} element={<LazySkolaBuducnostiPage />} />
                      <Route path={'/licenses/'} element={<LazyLicensesPage />} />
                      <Route path={GuidesPage.getRoute()} element={<GuidesPage />} />
                      <Route path={ClassPage.getRoute()} element={<ClassPage />} />
                      <Route path={UnitPage.unit.getRoute()} element={<UnitPage />} />
                      <Route path={UnitPage.module.getRoute()} element={<UnitPage />} />
                      <Route path={UnitPage.content.getRoute()} element={<UnitPage />} />
                      <Route path={(ArticlePage as any).getRoute()} element={<ArticlePage />} />
                      <Route path={SignInPage.getRoute()} element={<SignInPage />} />
                      <Route path={SignUpPage.getRoute()} element={<LazySignUpPage />} />
                      <Route path={'/password_reset/'} element={<LazyPasswordResetPage />} />
                      <Route path={'/settings/'} element={<LazySettingsPage />} />
                      <Route path={'/profile'} element={<LazyProfilePage />} />
                      <Route path={'/event-log/:userId?/'} element={<LazyEventLogPage />} />
                      <Route path={'/password_reset/done/'} element={<LazyPasswordResetDonePage />} />
                      <Route path={'/password_reset/:uid/:token/'} element={<LazyPasswordResetSetPasswordPage />} />
                      <Route path={'/password_reset/complete/'} element={<LazyPasswordResetCompletePage />} />
                      <Route path={ForbiddenPage.getRoute()} element={<ForbiddenPage />} />
                      <Route path={'/activate/:hash/:uid/:token/'} element={<LazyActivateSetPasswordPage />} />
                      <Route path={'/activate/:hash/done/'} element={<LazyActivateConfirmedPage />} />
                      <Route path={'/join/:code'} element={<LazyInviteCodePage />} />
                      <Route path={'/join/'} element={<LazyInviteCodeHomePage />} />
                      <Route path={'/info'} element={<InfoMainPage />} />
                      <Route path={'/explore'} element={<InfoMainPage />} />
                      <Route path={'/experience'} element={<InfoMainPage />} />
                      <Route path={'/info/:id'} element={<ProgramInfoPage />} />
                      <Route path={'/explore/:slug'} element={<ProgramInfoPage />} />
                      <Route path={'/experience/:slug'} element={<ProgramInfoPage />} />
                      <Route path={'/myprograms'} element={<LazyMyPrograms />} />
                      <Route path={'/assistants'} element={<LazyAssistants />} />
                      <Route path={'/assistants/:id'} element={<LazyAssistants />} />
                      <Route path={'/license-expired'} element={<LazyLicenseExpired />} />
                      <Route
                        path={SignOutPage.getRoute()}
                        element={
                          <>
                            <AuthContext.Consumer>
                              {(value) =>
                                value?.is_authenticated ? (
                                  <LoaderContainer>
                                    <GridLoader color={colors.darkblue} />
                                  </LoaderContainer>
                                ) : (
                                  <SignOutPage />
                                )
                              }
                            </AuthContext.Consumer>
                          </>
                        }
                      />
                      <Route path={'/legal/privacy'} element={<LazyPrivacyPage />} />
                      <Route path={'/legal/terms'} element={<LazyTermsPage />} />
                      <Route path={'/legal/accessibility'} element={<LazyAccessibilityStatementPage />} />
                      <Route path={Page404.getRoute()} element={<Page404 />} />
                      <Route path={'/interactive-test/'} element={<InteractiveTestPage />} />
                      <Route path={'/script-writer/'} element={<LazyEditorPage />} />
                      <Route path="*" element={<Page404 />} />
                    </Routes>
                  </Suspense>
                  </div>
                  <ModalContainer />
                </main>

                <footer className="footer">
                  <div className="footer-inner">
                    <div className="footer-legal">
                      <Text textStyle="finePrint" color="#767676" className="footer-copy">
                        {t('app.copyright')} {new Date().getFullYear()}. {t('app.rightsReserved')}
                      </Text>
                      <nav className="footer-links" aria-label="Legal">
                        <Link className="pdf-link" to="/legal/privacy" target="_blank">
                          {t('app.privacyPolicy')}
                        </Link>
                        <span className="footer-sep" aria-hidden="true">·</span>
                        <Link className="pdf-link" to="/legal/terms" target="_blank">
                          {t('app.termsOfUse')}
                        </Link>
                        <span className="footer-sep" aria-hidden="true">·</span>
                        <Link className="pdf-link" to="/legal/accessibility" target="_blank">
                          {t('app.accessibilityStatement')}
                        </Link>
                      </nav>
                    </div>
                    <div className="footer-utils">
                      <Changelog />
                      <Help />
                    </div>
                  </div>
                </footer>
                <ChatContainer />
              </Router>
            </CloudinaryContext>
          </DndProvider>
        </AuthProvider>
      </ChakraProvider>
    </div>
  );
})`
  * {
    box-sizing: border-box;
  }

  max-width: 2000px;
  margin: 0 auto;

  min-height: 100vh;

  ${NavContainer} {
    max-width: 2000px;
    width: 100% !important;
    padding-left: ${spacing.BASE_PLUS};
    padding-right: ${spacing.BASE_PLUS};
    margin-left: -${spacing.BASE};
    margin-right: -${spacing.BASE};
    &.marketplace {
      padding-left: 0px;
      padding-right: 0px;
      margin-left: 0px;
      margin-right: 0px;
    }
  }

  .main {
    min-height: calc(100vh - 60px);
  }

  .footer {
    background-color: rgb(238, 240, 248);
    border-top: 1px solid rgba(0, 0, 0, 0.06);

    .footer-inner {
      max-width: 2000px;
      margin: 0 auto;
      padding: ${spacing.SMALL} ${spacing.BASE_PLUS};
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px 16px;
      flex-wrap: wrap;
      ${media.tablet`
        padding: ${spacing.SMALL} ${spacing.BASE};
      `}
      ${media.phone`
        padding: 12px ${spacing.SMALL};
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
      `}
    }

    .footer-legal {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 4px 10px;
      min-width: 0;
    }

    .footer-copy {
      font-size: 12px;
      color: #767676;
      line-height: 1.5;
    }

    .footer-links {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 2px 6px;
    }

    .footer-sep {
      color: #b0b3c6;
      font-size: 11px;
    }

    .footer-utils {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;

      /* neutralise inline margins added by Changelog/Help */
      span[style] {
        margin-left: 0 !important;
      }
    }

    .pdf-link {
      color: #767676;
      text-decoration: underline;
      font-size: 12px;
      line-height: 1.5;

      &:hover {
        color: ${colors.darkblue};
      }
    }
  }
`;

export default App;
